// Date helpers. Everything is an ISO 'YYYY-MM-DD' string handled in UTC so the
// sprint day number never drifts with the founder's timezone or travel.

const MS_DAY = 86400000;

export function toISO(d) {
  if (typeof d === 'string') return d.slice(0, 10);
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
}

export function parseISO(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
}

export function todayISO(now) {
  const d = now ? new Date(now) : new Date();
  // Local calendar date, expressed as ISO — "today" should mean the founder's today.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(iso, n) {
  return toISO(new Date(parseISO(iso) + n * MS_DAY));
}

export function diffDays(a, b) {
  return Math.round((parseISO(b) - parseISO(a)) / MS_DAY);
}

export function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtDate(iso, opts = {}) {
  if (!iso) return '—';
  const ms = parseISO(iso);
  const d = new Date(ms);
  const day = d.getUTCDate();
  const mon = MONTHS[d.getUTCMonth()];
  return opts.year ? `${day} ${mon} ${d.getUTCFullYear()}` : `${day} ${mon}`;
}

export function fmtDateUpper(iso) {
  return fmtDate(iso).toUpperCase();
}

// ISO weekday: Mon = 1 ... Sun = 7
export function isoWeekday(iso) {
  const wd = new Date(parseISO(iso)).getUTCDay();
  return wd === 0 ? 7 : wd;
}

export function isWeekend(iso) {
  return isoWeekday(iso) >= 6;
}

// Monday of the week containing `iso`.
export function weekStart(iso) {
  return addDays(iso, -(isoWeekday(iso) - 1));
}

export function weekEnd(iso) {
  return addDays(weekStart(iso), 6);
}

/** Upcoming Monday on or after `from` (UTC calendar). If `from` is Monday, returns it. */
export function nextMondayISO(from = todayISO()) {
  const day = new Date(parseISO(from)).getUTCDay(); // 0=Sun … 6=Sat
  const delta = (1 - day + 7) % 7;
  return addDays(from, delta);
}

// Business days elapsed from `a` to `b`, exclusive of `a`, inclusive of `b`.
export function businessDaysBetween(a, b) {
  if (!a || !b) return 0;
  let n = 0;
  const dir = diffDays(a, b) >= 0 ? 1 : -1;
  let cur = a;
  while (cur !== b) {
    cur = addDays(cur, dir);
    if (!isWeekend(cur)) n += dir;
  }
  return n;
}

export function isBefore(a, b) {
  return parseISO(a) < parseISO(b);
}

export function isAfter(a, b) {
  return parseISO(a) > parseISO(b);
}

export function withinRange(iso, start, end) {
  const t = parseISO(iso);
  return t >= parseISO(start) && t <= parseISO(end);
}
